import { makeStyles } from '@mui/styles';
import IconButton from 'apollo-react/components/IconButton';
import Modal from 'apollo-react/components/Modal';
import Table, { compareStrings, createStringSearchFilter } from 'apollo-react/components/Table';
import Tooltip from 'apollo-react/components/Tooltip';
import Download from 'apollo-react-icons/Download';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { handleErrors } from '../../actions/ErrorActions';
import { getBulkImportActions } from '../../api/workflow-master-setup-import';
import { useTranslation } from '../../custom-hooks/useTranslation';
import TextFieldFilter from '../TableFilters/TextInputFilter';

const useStyles = makeStyles({
  modal: {
    minWidth: '65%',
    zIndex: 1200,
  },
  templatesTable: {
    border: 'solid 1px #e9e9e9',
    boxShadow: '0 4px 16px 0 rgb(0 0 0 / 4%)',
    '& thead > tr > th:nth-last-child(1) > svg': {
      display: 'none !important',
    },
  },
  loaderContainer: {
    paddingLeft: '5px',
  },
});

export const DownloadImportFileCell = ({
  row: {
    createStepId,
    actionName,
    entityKey,
    createContainerWorkflowKey,
    createWorkflowKey,
    setDownloadTemplateModalOpen,
    setApiParams,
  },
}) => {
  const translateString = useTranslation();

  const downloadClickHandler = async () => {
    setApiParams({
      createContainerWorkflowKey,
      createWorkflowKey,
      entityKey,
      actionName,
      createStepId,
    });
    setDownloadTemplateModalOpen(true);
  };

  return (
    <Tooltip title={translateString('Download')} placement="bottom">
      <IconButton onClick={downloadClickHandler} aria-label={translateString('Download')}>
        <Download />
      </IconButton>
    </Tooltip>
  );
};

const columns = [
  {
    header: 'Action Name',
    accessor: 'translatedActionName',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('translatedActionName'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Form Name',
    accessor: 'formName',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('formName'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Template File',
    accessor: 'TemplateFile',
    customCell: DownloadImportFileCell,
  },
];

const TemplateListModal = ({
  open,
  setModalOpen,
  entityName,
  workflowTemplateProps,
  setDownloadTemplateModalOpen,
  setApiParams,
}) => {
  const [isLoading, setLoading] = useState(false);
  const [importActions, setImportActions] = useState([]);

  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const rowsPerPageOptions = [5, 10, 15, translateString('All')];

  const loadImportActions = async () => {
    setLoading(true);
    const response = await getBulkImportActions(entityName);
    setLoading(false);

    if (response.hasError) {
      dispatch(handleErrors(response.errors));
      return;
    }

    const { bulkImportActions } = response;
    const preprocessedImportActions = bulkImportActions.map((action) => ({
      entityKey: entityName,
      actionName: action.id,
      translatedActionName: action.name,
      formName: action.formName,
      ...workflowTemplateProps,
      setDownloadTemplateModalOpen,
      setApiParams,
    }));

    setImportActions(preprocessedImportActions);
  };

  useEffect(() => {
    if (open) {
      loadImportActions();
    } else {
      setImportActions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      open={open}
      variant="default"
      onClose={() => setModalOpen(false)}
      title={translateString('Templates')}
      className={classes.modal}
      disableBackdropClick
      disableEscapeKeyDown
      hideButtons
    >
      <div className={classes.templatesTable}>
        <Table
          rowId="translatedActionName"
          columns={columns}
          rows={importActions}
          hasScroll
          isLoading={isLoading}
          emptyProps={{
            text: translateString('No data to display'),
          }}
          rowsPerPageOptions={rowsPerPageOptions}
          defaultRowsPerPage={10}
          showFilterIcon
          initialSortedColumn="translatedActionName"
          initialSortOrder="asc"
          tablePaginationProps={{
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} ${translateString('of')} ${count}`,
            truncate: true,
            labelRowsPerPage: translateString('Rows'),
            pageText: translateString('Page'),
            ofText: translateString('of'),
          }}
        />
      </div>
    </Modal>
  );
};

export default TemplateListModal;
